import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  // Override the parent's `requiredRefs = ['dialog']` so the base Component's
  // #updateRefs never throws MissingRefError for us. The dialog element goes
  // missing intermittently in real page loads for reasons outside our control:
  //
  // 1. Oxify / "IA Cart" app-embed re-initialises on a ~20s cycle and briefly
  //    rearranges our subtree.
  // 2. Microsoft Clarity monkey-patches `customElements.define` and can invoke
  //    connectedCallback in a context where the dialog hasn't been attached
  //    yet (visible as `HTMLElement.connectedCallback` in the stack).
  // 3. The base Component runs #updateRefs from an async MutationObserver
  //    callback — throws there escape any sync try/catch around super().
  //
  // With `requiredRefs = []` the ref lookup is best-effort: `this.refs.dialog`
  // may temporarily be undefined, and #openDrawer/#updateStickyState already
  // guard on that (falling back to Oxify's public API for the open path).
  requiredRefs = /** @type {string[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.#hydrateDeferred();
      this.#openDrawer();
    }
  };

  open() {
    this.#hydrateDeferred();
    this.#openDrawer();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  // Open the drawer — fall back to the Oxify (IA Cart) app's public API if
  // the theme's own <dialog> ref isn't currently bound (third-party app is
  // between re-init cycles). This keeps checkout reachable in all states.
  #openDrawer() {
    if (this.refs?.dialog) {
      this.showDialog();
      return;
    }
    const oxifyOpen = /** @type {any} */ (window).openOxifyCartDrawer;
    if (typeof oxifyOpen === 'function') {
      try { oxifyOpen(); } catch { /* fall through */ }
    }
  }

  // Hydrate the deferred Shopify custom elements the first time the drawer
  // opens. They are kept inert inside <template>s at page load so shop-js /
  // /checkout-web/ SPA is not eagerly preloaded on every homepage visit.
  #hydrateDeferred = () => {
    for (const template of this.querySelectorAll('template[data-deferred-checkout-template]')) {
      const container = template.parentElement;
      template.replaceWith(template.content.cloneNode(true));
      container?.removeAttribute('data-deferred-accelerated-checkout');
      container?.removeAttribute('data-deferred-payment-terms');
    }
  };

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

